/**
 * Burkhard-Keller tree for exact edit-distance retrieval.
 *
 * Built once over the vocabulary (at index load) and queried per token during
 * the fuzzy stage. A linear scan compares every vocabulary term against each
 * query token; the BK-tree prunes whole branches by the triangle inequality on
 * Levenshtein distance, visiting only terms within `maxDist`. The result set is
 * identical to a full scan (all terms with `levenshtein <= maxDist`), so the
 * fuzzy stage's scores are unchanged — only the cost drops.
 *
 * @see ../docs/search-pipeline.md — §3.4 (fuzzy stage).
 */
import { levenshtein } from './autocomplete.js';
export class BKTree {
    root = null;
    static build(terms) {
        const tree = new BKTree();
        for (const term of terms)
            tree.insert(term);
        return tree;
    }
    insert(term) {
        if (!this.root) {
            this.root = { term, children: new Map() };
            return;
        }
        let node = this.root;
        for (;;) {
            const d = levenshtein(term, node.term);
            if (d === 0)
                return; // duplicate term, skip
            const child = node.children.get(d);
            if (!child) {
                node.children.set(d, { term, children: new Map() });
                return;
            }
            node = child;
        }
    }
    /**
     * Return every term with `levenshtein(term, target) <= maxDist`, paired with
     * its exact distance. Order is not defined; callers sort by
     * confidence/term-frequency. Returning the distance avoids recomputing it at
     * the call site (the fuzzy stage derives confidence from it).
     */
    query(target, maxDist) {
        const result = [];
        if (!this.root)
            return result;
        const stack = [this.root];
        while (stack.length > 0) {
            const node = stack.pop();
            const d = levenshtein(target, node.term);
            if (d <= maxDist)
                result.push({ term: node.term, dist: d });
            const low = d - maxDist;
            const high = d + maxDist;
            for (const [dist, child] of node.children) {
                if (dist >= low && dist <= high)
                    stack.push(child);
            }
        }
        return result;
    }
}
//# sourceMappingURL=bk-tree.js.map